"use client";

import { getLink } from "@utils/linkUtils";
import { DateTimeFormatOptions, useFormatter, useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { useParams, usePathname } from "next/navigation";
import React, { FC, useContext, useEffect, useMemo, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import { ModalsContext } from "@/context/ModalsContext";
import { UTMParametersContext, getFallbackCampaignParameters } from "@/context/UTMParametersContext";
import { UserContext } from "@/context/UserContext";
import { contactChannelTracking, ctaClickTracking } from "@/dataLayer/dataLayer";
import { DL_KEYS } from "@/dataLayer/keys";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import useMediaQuery from "@/hooks/useMediaQuery";
import { Media } from "@/types";

import { handleContactChannelTracking } from "../ContactChannels/ContactChannels";
import TextHighlight from "../ui/TextHighlight/TextHighlight";
import { IntroInventoryData } from "./type";

const InventoryMapComponent: FC<Media> = (map) => {
  return (
    <>
      <Image src={map.url} width={340} height={259} alt={map.title || ""} />
    </>
  );
};

export const InventoryLpIntro: FC<IntroInventoryData> = ({
  entry,
  projectInfo,
  serverSideCookieCurrency,
  currencyFromApiRes,
  leadFormPageLocation,
}) => {
  const { currency } = useContext(UserContext);
  const format = useFormatter();
  const params = useParams();
  const pathname = usePathname();
  const t = useTranslations();
  const [buttonsBlack, setButtonsBlack] = useState(false);
  const [pageTitle, setPageTitle] = useState("");
  const { utmParameters } = useContext(UTMParametersContext);
  const locale = useLocale();
  const isSo = useIsSaleScreen();
  const [isLoadingForPrice, setIsLoadingForPrice] = useState(true);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const projectSlug = pathname?.split("/")?.find((segment, idx, arr) => arr[idx - 1] === "projects" || arr[idx - 1] === "communities");

  // Takes the name of the property from the metadata and sets the label that is sent with the WhatsApp message
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window?.location?.pathname.includes("projects")) {
        const title = document?.title;
        setPageTitle(title);
      }
    }
  }, []);

  // Leader for Currency
  useEffect(() => {
    if (serverSideCookieCurrency && currencyFromApiRes && currency) {
      if (serverSideCookieCurrency === currencyFromApiRes && currencyFromApiRes === currency) {
        setIsLoadingForPrice(false);
      } else {
        setIsLoadingForPrice(true);
      }
    }
  }, [serverSideCookieCurrency, currencyFromApiRes, currency]);

  const getLocalStorageParams = () => {
    if (typeof window !== "undefined") {
      const params = localStorage.getItem("utmParameters");
      if (params) {
        const parsedParams = JSON.parse(params);
        const searchParams = new URLSearchParams(parsedParams);
        return searchParams.toString();
      }
    }
    return "";
  };

  // Gets and set UTM Parameters
  const parameters: { [key: string]: string } = {
    ...getFallbackCampaignParameters(utmParameters?.utm_medium),
    ...utmParameters,
  };

  // const whatsappURL = useMemo(() => {
  //   if (typeof window !== "undefined") {
  //     const searchParams = new URLSearchParams(parameters);
  //     const landingPageURL = window.location.href;
  //     const localStorageParams = getLocalStorageParams();
  //     searchParams.delete("fullLandingPageUrl");
  //     searchParams.delete("landingPageURL");
  //     return {
  //       finalLandingPageURL: localStorageParams
  //         ? `${landingPageURL}?${localStorageParams}&promotions.damacproperties=false`
  //         : `${landingPageURL}?promotions.damacproperties=false`,
  //       searchParams: searchParams,
  //     };
  //   } else {
  //     return undefined;
  //   }
  // }, [parameters]);

  const whatsappURL = useMemo(() => {
    if (typeof window !== "undefined") {
      // Get base URL without query parameters
      const currentUrl = new URL(window.location.href);
      const baseUrl = `${currentUrl.origin}${currentUrl.pathname}`;

      // Parse existing URL parameters
      const currentParams = new URLSearchParams(currentUrl.search);

      // Get localStorage parameters
      const localStorageParams = getLocalStorageParams();

      // Properly type the object with index signature
      const localStorageParamsObj: Record<string, string> = {};

      if (localStorageParams) {
        // Convert localStorageParams string to object
        const searchParams = new URLSearchParams(localStorageParams);
        searchParams.forEach((value, key) => {
          localStorageParamsObj[key] = value;
        });
      }

      // Create a new URLSearchParams instance for the final parameters
      const finalParams = new URLSearchParams();

      // Priority: localStorage parameters over URL parameters
      // Add current URL parameters first (lower priority)
      currentParams.forEach((value, key) => {
        finalParams.append(key, value);
      });

      // Then override with localStorage parameters (higher priority)
      Object.entries(localStorageParamsObj).forEach(([key, value]) => {
        finalParams.set(key, String(value)); // Explicitly convert to string
      });

      // Construct the final landing page URL - CHECK IF PARAMS EXIST TO AVOID ?&
      const paramsString = finalParams.toString();
      const finalLandingPageURL = paramsString ? `${baseUrl}?${paramsString}` : baseUrl;

      // Create search params from campaign parameters
      const searchParams = new URLSearchParams(
        parameters["campaign_id"] === "a120Y000000uLMj" ? { campaign_id: parameters["campaign_id"] } : parameters,
      );
      searchParams.delete("fullLandingPageUrl");
      searchParams.delete("landingPageURL");

      // Check if the URL already contains utm_medium or campaign_id
      // Parse the finalLandingPageURL to check for duplicate parameters
      const finalUrlObj = new URL(finalLandingPageURL);
      const finalUrlParams = finalUrlObj.searchParams;

      // When constructing the final WhatsApp URL text, check if parameters already exist
      const textUrlParams = new URLSearchParams(searchParams.toString());

      // Specifically check and remove duplicates for utm_medium and campaign_id
      if (finalUrlParams.has("utm_medium") && textUrlParams.has("utm_medium")) {
        // Keep the one from finalUrlParams (from localStorage/URL) and remove from textUrlParams
        textUrlParams.delete("utm_medium");
      }

      if (finalUrlParams.has("campaign_id") && textUrlParams.has("campaign_id")) {
        // Keep the one from finalUrlParams (from localStorage/URL) and remove from textUrlParams
        textUrlParams.delete("campaign_id");
      }

      return {
        finalLandingPageURL,
        searchParams: textUrlParams,
      };
    }
    return undefined;
  }, [parameters]);

  const backgroundStyle = {
    ...{
      backgroundImage: `url(${entry?.background?.url})`,
      backgroundRepeat: `no-repeat`,
      backgroundSize: `cover`,
      backgroundPosition: `bottom right`,
    },
  };

  const minPriceFormatted = useMemo(() => {
    if (projectInfo?.minPrice) {
      // return format
      //   .number(Math.round(projectInfo.minPrice / 100), {
      //     style: "currency",
      //     currency: currency,
      //     trailingZeroDisplay: "stripIfInteger",
      //     currencyDisplay: locale?.split("-")[1] === "ae" ? "code" : "symbol",
      //   })
      //   ?.replace("UK", "");
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        trailingZeroDisplay: "stripIfInteger",
        currencyDisplay: locale?.split("-")[1] === "ae" ? "code" : "symbol",
      })
        .format(Math.round(Number(projectInfo.minPrice / 100)) || 0)
        ?.replace(/(\p{Sc})(\d)/u, "$1 $2")
        ?.replaceAll("UK", "");
    }
    return;
  }, [currency, format, projectInfo?.minPrice]);

  const deliveryDateFormatted = useMemo(() => {
    if (projectInfo?.deliveryDate) {
      const date = new Date(projectInfo.deliveryDate);
      const options = { month: "long", year: "numeric" } as DateTimeFormatOptions;
      return date.toLocaleDateString(params.locale, options);
    }
    return;
  }, [params?.locale, projectInfo?.deliveryDate]);

  // if the color is not white change the buttons theme to black
  useEffect(() => {
    const root = document.querySelector(".font-primary-override-container")! || document.querySelector(".font-primary-override-container-faq")!;
    const mainColor = root ? getComputedStyle(root)?.getPropertyValue("--font-primary-color").trim() : "";
    if (root && mainColor != "#ffffff") {
      setButtonsBlack(true);
    }
  }, []);

  const { setGetInTouchModalOpen, setGetFormPageLocation } = useContext(ModalsContext);
  const getInTouchHref = entry?.getInTouch ? getLink(entry?.getInTouch, locale) : undefined;
  const whatsappNumber = t("label.whatsapp.number") || "97144016335";

  return (
    <section style={backgroundStyle}>
      <article className="mx-auto p-m pt-[40px] pb-[40px]  md:p-0 grid gap-x-xxl md:w-20\/24 md:grid-flow-col md:grid-cols-2 md:auto-rows-auto md:gap-x-3xl md:pt-[80px] md:pb-[30px]">
        <div className="md:col-2 md:order-2">
          <div className="flex flex-col gap-xxl md:gap-3xl">
            <h1 className="text-xl font-semibold uppercase font-primary tracking-[2px] md:tracking-[3.2px] accent-font accent-title accent-color-strong">
              <TextHighlight text={entry?.title} />
            </h1>
            {entry?.description && <div className="font-secondary-light md:w-8/12">{entry?.description}</div>}
          </div>
        </div>
        <div className="mt-xxl md:mt-0 md:row-span-3 md:order-1">
          <ul className="flex flex-col gap-xl md:gap-xxl">
            {entry?.kpIs?.map((kpi, index) => (
              <li key={index}>
                <p className="font-secondary text-s" style={{ marginBottom: "8px" }}>
                  {kpi.description}
                </p>
                <p className="font-semibold  uppercase font-primary text-l primary-color accent-font accent-title">
                  {kpi.fetchHandoverDateFromCommercetools && !!deliveryDateFormatted
                    ? deliveryDateFormatted
                    : kpi.fetchMinPriceFromCommercetools && !!minPriceFormatted
                      ? isLoadingForPrice
                        ? t("common.loading")
                        : minPriceFormatted
                      : kpi.title}
                </p>
              </li>
            ))}
          </ul>
          {entry?.communityMapImage && (
            <div className="hidden  mt-[60px] md:block">
              <InventoryMapComponent url={entry?.communityMapImage.url} title={entry?.communityMapImage?.title} />
            </div>
          )}
        </div>
        <div className="mt-3xl md:col-2 md:row-2 md:order-3 flex gap-m justify-start">
          {!isSo && entry?.getInTouch && (
            <Button
              type="button"
              href={getInTouchHref !== "#" ? getInTouchHref : undefined}
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.SECONDARY}
              theme={buttonsBlack ? ButtonTheme.BLACK : ButtonTheme.WHITE}
              customClass="h-[44px] min-w-[`${isDesktop? 210px : 180px}`] md:min-w-fit text-center"
              onClick={() => {
                // BUG 93052
                if (getInTouchHref === "#") {
                  setGetInTouchModalOpen(true);
                  setGetFormPageLocation(leadFormPageLocation);
                }
                handleContactChannelTracking("calendar", projectSlug || "");
                ctaClickTracking(entry?.getInTouch?.label || "Get in touch", "ContactChannels", "ContactChannels");
              }}
            >
              {entry?.getInTouch?.label}
            </Button>
          )}
          {/* WhatsApp button with UTM Parameters */}
          {!isSo && (
            <>
              <Button
                customClass="w-[44px] h-[44px] flex items-center justify-center stroke-white whatsapp-button"
                size={ButtonSize.MEDIUM}
                theme={buttonsBlack ? ButtonTheme.BLACK : ButtonTheme.WHITE}
                type="button"
                rounded={true}
                variant={ButtonVariant.SECONDARY}
                href={`tel:${getFallbackCampaignParameters(utmParameters?.utm_medium)?.virtual_number || "+97145205400"}`}
                poneNumberCta={() => {
                  contactChannelTracking(DL_KEYS?.callUs, projectSlug || "");
                  // ctaClickTracking(t("label.common.callus"), "ContactChannels", "ContactChannels");
                  ctaClickTracking(DL_KEYS?.callUs, "ContactChannels", "ContactChannels");
                }}
              >
                <Icon name="phone" size="24" title={t("label.common.phone")} />
              </Button>
              <Button
                customClass="w-[44px] h-[44px] flex items-center justify-center stroke-white whatsapp-button"
                size={ButtonSize.MEDIUM}
                theme={buttonsBlack ? ButtonTheme.BLACK : ButtonTheme.WHITE}
                type="button"
                rounded={true}
                variant={ButtonVariant.SECONDARY}
                href={
                  !!whatsappURL
                    ? `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${
                        pathname?.includes("projects")
                          ? encodeURIComponent(
                              t("text.project.pages", {
                                projectName: pageTitle,
                                url: `${whatsappURL?.finalLandingPageURL}${whatsappURL?.finalLandingPageURL?.includes("?") ? "&" : "?"}${whatsappURL?.searchParams}`,
                              }),
                            )
                          : encodeURIComponent(
                              t("text.generic.pages", {
                                url: `${whatsappURL?.finalLandingPageURL}${whatsappURL?.finalLandingPageURL?.includes("?") ? "&" : "?"}${whatsappURL?.searchParams}`,
                              }),
                            )
                      }`
                    : t("text.generic.pages", {
                        url: `https://api.whatsapp.com/send?phone=${whatsappNumber}&campaign_id=${parameters["campaign_id"]}`,
                      })
                }
                onClick={() => {
                  handleContactChannelTracking("social-whatsapp", projectSlug || "");
                  ctaClickTracking("WhatsApp", "ContactChannels", "ContactChannels");
                }}
              >
                <Icon name="social-whatsapp" size="24" title="WhatsApp" />
              </Button>
            </>
          )}
          {!isSo &&
            entry?.channels?.map((contact) => (
              <Button
                key={contact.id}
                type="button"
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.SECONDARY}
                theme={buttonsBlack ? ButtonTheme.BLACK : ButtonTheme.WHITE}
                // TODO: check if the correct href should be this one
                // href={contact.link.icon === "phone.svg" ? `tel:${contact.link.url}` : contact.link.url}
                href={contact.link.url}
                rounded={true}
                customClass="w-[44px] h-[44px] flex items-center justify-center stroke-white"
                onClick={() => {
                  handleContactChannelTracking(contact.link.icon || "", projectSlug || "");
                }}
              >
                {contact?.link?.icon && <Icon size="24" name={contact.link.icon} title={contact.link.icon} />}
              </Button>
            ))}
        </div>
      </article>
      {entry.communityMapImage && (
        <div className="flex flex-col gap-xxl pt-xl pb-xl items-center md:hidden">
          <InventoryMapComponent url={entry.communityMapImage.url} title={entry.communityMapImage.title} />
        </div>
      )}
    </section>
  );
};

export default InventoryLpIntro;
